import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × SUZAN AI: Trust couture (meta mode)
// Luminous grids, secure tessellation, soft data gradients
export function VasarelySuzanLuxuryMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Ethereal gradient background */}
      <defs>
        <linearGradient id="suzan-luxury" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={brand.deep} />
          <stop offset="50%" stopColor="#1A1F3A" />
          <stop offset="100%" stopColor={brand.deep} />
        </linearGradient>
        <radialGradient id="suzan-glow">
          <stop offset="0%" stopColor={brand.aqua} stopOpacity="0.4" />
          <stop offset="50%" stopColor={brand.violet} stopOpacity="0.2" />
          <stop offset="100%" stopColor={brand.deep} stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="794" height="1123" fill="url(#suzan-luxury)" />

      <g transform="translate(397 540)">
        {/* Luminous grid (trust architecture) */}
        {Array.from({ length: 14 }).map((_, i) =>
          Array.from({ length: 18 }).map((_, j) => {
            const x = (i - 6.5) * 44;
            const y = (j - 8.5) * 42;
            const luminosity = Math.sin(i * 0.4) * Math.cos(j * 0.3);
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 18}
                y={y - 18}
                width="36"
                height="36"
                fill={luminosity > 0 ? brand.aqua : brand.violet}
                opacity={Math.abs(luminosity) * 0.2}
                stroke={luminosity > 0 ? brand.aqua : brand.violet}
                strokeWidth="1"
              />
            );
          })
        )}

        {/* Secure tessellation (hexagonal trust) */}
        {[0, 1, 2].map((ring) => {
          const hexCount = ring === 0 ? 1 : ring * 6;
          return Array.from({ length: hexCount }).map((_, i) => {
            const angle = (i * Math.PI * 2) / hexCount;
            const r = ring * 90;
            const cx = Math.cos(angle) * r;
            const cy = Math.sin(angle) * r;
            
            return (
              <g key={`${ring}-${i}`}>
                {Array.from({ length: 6 }).map((_, k) => {
                  const hAngle = k * 60;
                  const hRad = (hAngle * Math.PI) / 180;
                  const nextHRad = ((hAngle + 60) * Math.PI) / 180;
                  const size = 30;
                  
                  return (
                    <line
                      key={k}
                      x1={cx + Math.cos(hRad) * size}
                      y1={cy + Math.sin(hRad) * size}
                      x2={cx + Math.cos(nextHRad) * size}
                      y2={cy + Math.sin(nextHRad) * size}
                      stroke={brand.aqua}
                      strokeWidth="2"
                      opacity="0.4"
                    />
                  );
                })}
                <circle cx={cx} cy={cy} r="8" fill={brand.violet} opacity="0.6" />
              </g>
            );
          });
        })}

        {/* Soft data gradients (AI harmony) */}
        {Array.from({ length: 12 }).map((_, i) => {
          const y = -240 + i * 40;
          const points = Array.from({ length: 40 }).map((_, j) => {
            const x = -280 + j * 14;
            const gradient = Math.sin(j * 0.2 + i * 0.4) * 22;
            return `${x},${y + gradient}`;
          }).join(" ");
          
          return (
            <polyline
              key={i}
              points={points}
              fill="none"
              stroke={i % 2 === 0 ? brand.aqua : brand.violet}
              strokeWidth="2"
              opacity="0.35"
            />
          );
        })}

        {/* Trust circles (governance layers) */}
        {Array.from({ length: 16 }).map((_, i) => {
          const r = 40 + i * 16;
          return (
            <circle
              key={i}
              cx="0"
              cy="0"
              r={r}
              fill="none"
              stroke={i % 2 === 0 ? brand.aqua : brand.violet}
              strokeWidth="1.5"
              opacity={0.35 - i * 0.018}
            />
          );
        })}

        {/* Haute governance nodes */}
        {[0, 51.43, 102.86, 154.29, 205.71, 257.14, 308.57].map((angle) => {
          const rad = (angle * Math.PI) / 180;
          const r = 200;
          
          return (
            <g key={angle}>
              <circle
                cx={Math.cos(rad) * r}
                cy={Math.sin(rad) * r}
                r="50"
                fill="url(#suzan-glow)"
              />
              <circle
                cx={Math.cos(rad) * r}
                cy={Math.sin(rad) * r}
                r="16"
                fill={brand.aqua}
                opacity="0.6"
              />
              <circle
                cx={Math.cos(rad) * r}
                cy={Math.sin(rad) * r}
                r="22"
                fill="none"
                stroke={brand.violet}
                strokeWidth="2"
                opacity="0.6"
              />
            </g>
          );
        })}

        {/* Central couture emblem */}
        <circle cx="0" cy="0" r="80" fill="url(#suzan-glow)" />
        <circle cx="0" cy="0" r="60" fill={brand.aqua} opacity="0.3" />
        <circle cx="0" cy="0" r="60" fill="none" stroke={brand.aqua} strokeWidth="3" opacity="0.8" />
        <circle cx="0" cy="0" r="50" fill="none" stroke={brand.violet} strokeWidth="2" opacity="0.6" />
        
        {/* AI symbol */}
        <text
          x="0"
          y="8"
          textAnchor="middle"
          fontFamily="sans-serif"
          fontWeight="700"
          fontSize="28"
          fill={brand.white}
          opacity="0.9"
        >
          AI
        </text>
      </g>

      {/* Suzan AI text */}
      <text x="397" y="1030" textAnchor="middle" fontFamily="serif" fontSize="12" fill={brand.aqua} opacity="0.8" fontStyle="italic">
        trust couture — gouvernance comme horlogerie
      </text>
    </g>
  );
}
